/**
 * markdownConversionHelpers.ts
 *
 * Pure functions for converting between carousel ContentSlide[] and a
 * human-readable markdown string that users can edit in the text view.
 *
 * Exported so they can be unit-tested independently of the React component.
 */

// ── Types (duplicated here so this file has zero React/axios deps) ────────────

export interface EditableFieldMeta {
  key: string;
  inputType: 'text' | 'textarea' | 'list';
}

export interface ContentSlide {
  slotType: string;
  fields: Record<string, any>;
}

export interface CarouselStructure {
  templateId: string;
  themeId: string;
  slides: ContentSlide[];
  editableFields: Record<string, EditableFieldMeta[]>;
}

// ── Label maps ────────────────────────────────────────────────────────────────

export const SLOT_LABELS: Record<string, string> = {
  hook:                'Hook',
  interrupt:           'Pattern Break',
  'pattern-interrupt': 'Pattern Break',
  body:                'Point',
  'body-tip':          'Point',
  'body-point':        'Point',
  tip:                 'Point',
  cta:                 'Call to Action',
  brand:               'Brand',
};

const FIELD_LABELS: Record<string, string> = {
  headline:   'Headline',
  body:       'Description',
  hook:       'Opening line',
  tagline:    'Tagline',
  badge:      'Badge',
  points:     'Points',
  tips:       'Points',
  cta_text:   'CTA text',
  cta:        'CTA text',
  subheading: 'Subheading',
  caption:    'Caption',
  statement:  'Statement',
};

export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Slot descriptor type ──────────────────────────────────────────────────────

interface SlotDescriptor {
  slideIdx:   number;
  fields:     EditableFieldMeta[];
  label:      string;
  isEditable: boolean;
}

// ── buildAllSlots ─────────────────────────────────────────────────────────────

/**
 * Returns descriptors for ALL slides in order, including non-editable ones
 * (e.g. brand, system). The `isEditable` flag distinguishes them.
 *
 * This is the canonical source for labels — both slidesToMarkdown and
 * markdownToSlides use it to ensure label generation is consistent.
 */
export function buildAllSlots(
  slides: ContentSlide[],
  editableFields: Record<string, EditableFieldMeta[]>,
): SlotDescriptor[] {
  const result: SlotDescriptor[] = [];
  const slotCounters: Record<string, number> = {};

  for (let i = 0; i < slides.length; i++) {
    const slide      = slides[i];
    const fields     = editableFields[slide.slotType] ?? [];
    const isEditable = fields.length > 0;

    const baseLabel = SLOT_LABELS[slide.slotType]
      ?? slide.slotType.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const isPoint = baseLabel === 'Point';
    if (isPoint) slotCounters[slide.slotType] = (slotCounters[slide.slotType] ?? 0) + 1;
    const label = isPoint ? `${baseLabel} ${slotCounters[slide.slotType]}` : baseLabel;

    result.push({ slideIdx: i, fields, label, isEditable });
  }

  return result;
}

// ── buildEditableSlots ────────────────────────────────────────────────────────

/**
 * Convenience wrapper: same as buildAllSlots but filters to editable slides only.
 * Used internally by the slide-card view (StepContent) which skips non-editable slides.
 */
export function buildEditableSlots(
  slides: ContentSlide[],
  editableFields: Record<string, EditableFieldMeta[]>,
): Array<{ slideIdx: number; fields: EditableFieldMeta[]; label: string }> {
  return buildAllSlots(slides, editableFields)
    .filter(s => s.isEditable)
    .map(({ slideIdx, fields, label }) => ({ slideIdx, fields, label }));
}

// ── slidesToMarkdown ──────────────────────────────────────────────────────────

/**
 * Convert an array of carousel content slides to a human-readable markdown
 * string. ALL slides are included — editable slides show their fields, while
 * non-editable slides (e.g. brand, cover) show a read-only placeholder so the
 * user can see they exist and understand the total slide count.
 *
 * Format:
 *   ## Slide Label
 *
 *   Field Name: value
 *   Another Field: value
 *
 *   ---
 *
 *   ## Brand
 *
 *   (no editable fields)
 *
 *   ---
 *
 *   ## Next Slide
 *   ...
 */
export function slidesToMarkdown(
  slides: ContentSlide[],
  editableFields: Record<string, EditableFieldMeta[]>,
): string {
  const allSlots = buildAllSlots(slides, editableFields);
  const parts: string[] = [];

  for (const { slideIdx, fields, label, isEditable } of allSlots) {
    const slide = slides[slideIdx];
    const lines: string[] = [`## ${label}`, ''];

    if (!isEditable) {
      // Show a placeholder so the section is visible in the text view.
      // Internal field values are not exposed — they are not user-editable.
      lines.push('(no editable fields)');
    } else {
      for (const { key, inputType } of fields) {
        const lbl = fieldLabel(key);
        const val = slide.fields[key];

        if (inputType === 'list' && Array.isArray(val)) {
          lines.push(`${lbl}:`);
          for (const item of val) lines.push(`- ${item}`);
          lines.push('');
        } else {
          lines.push(`${lbl}: ${val ?? ''}`);
          lines.push('');
        }
      }

      // Trim trailing blank lines
      while (lines[lines.length - 1] === '') lines.pop();
    }

    parts.push(lines.join('\n'));
  }

  return parts.join('\n\n---\n\n');
}

// ── markdownToSlides ──────────────────────────────────────────────────────────

/**
 * Parse a (possibly user-edited) markdown string back into ContentSlide[].
 *
 * Matching strategy — label-based (not positional):
 *   1. Build the expected `## Label` for ALL slides (editable + non-editable),
 *      creating a label → slot mapping.
 *   2. For each section in the edited markdown, extract its `## Heading` and
 *      look it up in the label map.
 *   3. Editable matched slides: update their field values from the section.
 *   4. Non-editable slides: always preserved unchanged — their section content
 *      is ignored and they are never removed, even if absent from the markdown.
 *   5. Editable slides absent from the markdown: REMOVED from the result
 *      (deleting a `## Section` block removes that slide).
 *
 * Fallback: if NO editable headings matched, fall back to positional mapping
 * of editable slots only, so partial edits still work.
 */
export function markdownToSlides(
  markdown: string,
  originalSlides: ContentSlide[],
  editableFields: Record<string, EditableFieldMeta[]>,
): ContentSlide[] {
  // Deep-clone originals so we never mutate the source
  const result: ContentSlide[] = JSON.parse(JSON.stringify(originalSlides));

  // Build ALL slot descriptors (editable + non-editable)
  const allSlots = buildAllSlots(originalSlides, editableFields);
  if (allSlots.length === 0) return result;

  // Split into sections
  const sections = markdown
    .split(/\n---\n/)
    .map(s => s.trim())
    .filter(Boolean);

  if (sections.length === 0) return result;

  // Extract the ## Heading from each section (lower-cased for matching)
  const sectionHeadings = sections.map(s => {
    const first = s.split('\n')[0].trim();
    return first.startsWith('## ') ? first.slice(3).trim().toLowerCase() : '';
  });

  // Build a label (lower-cased) → slot index map across ALL slots
  const labelToSlotIdx = new Map<string, number>();
  for (let i = 0; i < allSlots.length; i++) {
    labelToSlotIdx.set(allSlots[i].label.toLowerCase(), i);
  }

  // Match sections to slots by heading; track which slots are covered
  const slotToSection = new Map<number, number>(); // slotIdx → sectionIdx
  for (let si = 0; si < sections.length; si++) {
    const heading = sectionHeadings[si];
    const slotIdx = heading ? labelToSlotIdx.get(heading) : undefined;
    if (slotIdx !== undefined && !slotToSection.has(slotIdx)) {
      slotToSection.set(slotIdx, si);
    }
  }

  // Fallback: if no EDITABLE headings matched, use positional mapping for
  // editable slots only. This handles the case where the user changed all headings.
  const editableSlotIndices = allSlots
    .map((s, i) => (s.isEditable ? i : -1))
    .filter(i => i >= 0);
  const matchedEditableCount = editableSlotIndices.filter(i => slotToSection.has(i)).length;

  if (matchedEditableCount === 0) {
    for (let si = 0; si < sections.length && si < editableSlotIndices.length; si++) {
      slotToSection.set(editableSlotIndices[si], si);
    }
  }

  // Update field values for matched EDITABLE slots only.
  // Non-editable slot sections are recognised but their content is ignored.
  for (const [slotIdx, sectionIdx] of slotToSection) {
    const { slideIdx, fields, isEditable } = allSlots[slotIdx];
    if (!isEditable) continue;

    const slide = result[slideIdx];

    // Build lowercase label → field meta map for this slide type
    const labelToField: Record<string, EditableFieldMeta> = {};
    for (const f of fields) {
      labelToField[fieldLabel(f.key).toLowerCase()] = f;
    }

    parseSectionIntoSlide(sections[sectionIdx], slide, labelToField);
  }

  // Remove EDITABLE slides whose section was absent from the markdown.
  // Non-editable slides are ALWAYS preserved regardless of whether their
  // section appeared — brand/cover slides are structural and cannot be removed.
  const removedIndices = allSlots
    .filter((slot, si) => slot.isEditable && !slotToSection.has(si))
    .map(s => s.slideIdx)
    .sort((a, b) => b - a); // high → low so earlier indices stay valid while splicing

  for (const idx of removedIndices) {
    result.splice(idx, 1);
  }

  return result;
}

// ── parseSectionIntoSlide (internal) ─────────────────────────────────────────

/**
 * Parse one markdown section and write field values directly into `slide`.
 * Handles text, textarea, and list field types.
 */
function parseSectionIntoSlide(
  section: string,
  slide: ContentSlide,
  labelToField: Record<string, EditableFieldMeta>,
): void {
  const lines = section.split('\n');
  let listKey: string | null = null;
  let listItems: string[] = [];

  const flushList = () => {
    if (listKey !== null) {
      slide.fields[listKey] = listItems;
      listKey = null;
      listItems = [];
    }
  };

  for (const line of lines) {
    // Slide heading — skip
    if (line.startsWith('## ')) { flushList(); continue; }

    // Bullet list item belonging to the current list field
    if (line.startsWith('- ') && listKey !== null) {
      listItems.push(line.slice(2).trim());
      continue;
    }

    // Blank line — flush any open list
    if (line.trim() === '') { flushList(); continue; }

    // "Label: value" line
    const colonIdx = line.indexOf(':');
    if (colonIdx > 0) {
      flushList();
      const lbl  = line.slice(0, colonIdx).trim().toLowerCase();
      const val  = line.slice(colonIdx + 1).trim();
      const meta = labelToField[lbl];
      if (meta) {
        if (meta.inputType === 'list') {
          listKey   = meta.key;
          listItems = val ? [val] : []; // value on same line as colon is first item
        } else {
          slide.fields[meta.key] = val;
        }
      }
    }
  }

  flushList(); // flush any list that runs to end of section
}
